import { environment } from "../config/environment";

class GoogleMapsService {
  private googleInstance: any = null;
  private isLoaded: boolean = false;
  private loadingPromise: Promise<any> | null = null;

  // Load the Google Maps API
  loadMapsApi(): Promise<any> {
    if (this.isLoaded && this.googleInstance) {
      return Promise.resolve(this.googleInstance);
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if the API is already loaded
      if (window.google && window.google.maps) {
        this.isLoaded = true;
        this.googleInstance = window.google;
        resolve(this.googleInstance);
        return;
      }

      // Create callback function
      const callbackName = 'googleMapsCallback';
      (window as any)[callbackName] = () => {
        this.isLoaded = true;
        this.googleInstance = window.google;
        resolve(this.googleInstance);
        delete (window as any)[callbackName];
      };

      // Create script element
      const script = document.createElement('script');
      const apiKey = environment.googleMapsApiKey.split('key=')[1]; // Extract just the key
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&loading=async`;
      script.async = true;
      script.defer = true;
      script.onerror = (error) => {
        reject(error);
        delete (window as any)[callbackName];
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  // Get the Google instance after the API is loaded
  getGoogleInstance(): any {
    return this.googleInstance;
  }

  // Add new method to add a marker to the map
  addMarkerToMap(map: any, position: { lat: number, lng: number }, options: any = {}): any {
    const defaultOptions = {
      map: map,
      position: position,
      animation: this.googleInstance.maps.Animation.DROP,
      icon: {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: '#F5A623',  // Orange for jobsite
        fillOpacity: 1,
        strokeColor: '#D48C1F',
        strokeWeight: 1,
        scale: 2,
        anchor: new this.googleInstance.maps.Point(12, 24)
      }
    };

    return new this.googleInstance.maps.Marker({
      ...defaultOptions,
      ...options
    });
  }

  // Add method to create and open info window
  createInfoWindow(content: string, marker: any, map: any): void {
    const infoWindow = new this.googleInstance.maps.InfoWindow({
      content: content
    });
    infoWindow.open(map, marker);
  }
}

// Add this interface to the file
declare global {
  interface Window {
    google: any;
  }
}

// Export a singleton instance
const googleMapsService = new GoogleMapsService();
export default googleMapsService;
